import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { animate, motion } from 'framer-motion';
import clsx from 'clsx';
import type { IQuestion } from '@/types/features/questionnaire/question';
import SingleChoiceOptions from '@/features/funnel/questionnaires/components/SingleChoiceOptions';
import MultipleChoiceOptions from '@/features/funnel/questionnaires/components/MultipleChoiceOptions';
import Input from '@/components/forms/Input';
import '@/styles/iief.css';
import SanitizedHtml from '@/components/ui/SanitizedHtml';

interface QuestionnaireContainerProps extends React.InputHTMLAttributes<HTMLInputElement> {
  questions: IQuestion[];
  getQuestionAnswer: (answer: Record<string, string[]>) => void;
  questionAnswer: Record<string, string[]>;
  setHasSafetyConcern?: Dispatch<SetStateAction<Record<string, boolean>>>;
  expandableTextareaContent?: Record<string, Record<string, string>>;
  setExpandableTextareaContent?: Dispatch<SetStateAction<Record<string, Record<string, string>>>>;
  expandableTextareaErrorMessage?: Record<string, string>;
}

const QuestionnaireContainer = ({
  questions,
  getQuestionAnswer,
  questionAnswer,
  setHasSafetyConcern,
  expandableTextareaContent,
  setExpandableTextareaContent,
  expandableTextareaErrorMessage,
}: QuestionnaireContainerProps) => {
  const smoothScroll = useCallback(
    (nextQuesIndex: number) => {
      if (nextQuesIndex <= questions.length - 1) {
        const nextQuestionId = questions[nextQuesIndex].id || '1';
        const targetY = (document.getElementById(nextQuestionId)?.offsetTop || 0) as number;

        if (targetY) {
          animate(window.scrollY, targetY, {
            duration: 1.1,
            ease: 'easeInOut',
            onUpdate: (value) => window.scrollTo(0, value),
          });
        }
      }
    },
    [questions],
  );

  const handleOnChange = useCallback(
    (questionId: string, answer: string | string[], nextQuesIndex: number) => {
      getQuestionAnswer({
        ...questionAnswer,
        [questionId]: Array.isArray(answer) ? answer : [answer],
      });
      smoothScroll(nextQuesIndex);
    },
    [getQuestionAnswer, smoothScroll, questionAnswer],
  );

  const updateState = useCallback(<T,>(setter?: Dispatch<SetStateAction<Record<string, T>>>) => {
    return (questionId: string, value: T) => {
      if (setter) {
        setter((prev) => {
          const existing = prev[questionId] || {};
          return {
            ...prev,
            [questionId]: {
              ...existing,
              ...value,
            },
          };
        });
      }
    };
  }, []);

  const handleSafetyConcern = (questionId: string, value: boolean) => {
    if (setHasSafetyConcern) {
      setHasSafetyConcern((prev) => ({ ...prev, [questionId]: value }));
    }
  };
  const handleExpandableTextarea = updateState(setExpandableTextareaContent);

  return (
    <div className={`flex flex-col w-full ${questions.length > 1 ? 'main-conf' : ''}`}>
      {questions.map((question, index) => (
        <motion.div
          key={question.id}
          id={question.id}
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.2 }}
        >
          <SanitizedHtml
            html={question.question_text}
            className={clsx(
              'text-subtitle text-ashgray mb-32 tablet:mb-40 mt-8 tablet:mt-32 question-title',
            )}
          />
          <div className="relative flex items-center">
            {question.question_type === 'multiple_choice' && (
              <MultipleChoiceOptions
                options={question.answer_options || []}
                selectedOptions={(questionAnswer[question.id] as string[]) || []}
                onChange={(answer) => handleOnChange(question.id, answer, index + 1)}
                setHasSafetyConcern={(concern) => handleSafetyConcern(question.id, concern)}
                expandableTextareaContent={expandableTextareaContent?.[question.id]}
                setExpandableTextareaContent={(content, optionId) =>
                  handleExpandableTextarea(question.id, {
                    [optionId]: content,
                  })
                }
                expandableTextareaErrorMessage={expandableTextareaErrorMessage}
                answerDisplayType={question.answer_display_type}
              />
            )}
            {question.question_type === 'single_choice' && (
              <SingleChoiceOptions
                options={question.answer_options || []}
                selectedOption={questionAnswer[question.id]?.[0] || ''}
                onChange={(answer) => handleOnChange(question.id, answer, index + 1)}
                setHasSafetyConcern={(concern) => handleSafetyConcern(question.id, concern)}
                expandableTextareaContent={
                  expandableTextareaContent?.[question.id]?.[questionAnswer[question.id]?.[0]] || ''
                }
                setExpandableTextareaContent={(content) =>
                  handleExpandableTextarea(question.id, {
                    [questionAnswer[question.id]?.[0]]: content,
                  })
                }
                expandableTextareaErrorMessage={expandableTextareaErrorMessage}
                answerDisplayType={question.answer_display_type}
              />
            )}
            {question.question_type === 'plain_text' && (
              <Input
                label=""
                type="text"
                placeholder="Enter your answer"
                onChange={(event) => handleOnChange(question.id, event.target.value, index + 1)}
                value={questionAnswer[question.id] || ''}
                containerClassName="!h-[48px] tablet:h-[60px]"
              />
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default QuestionnaireContainer;
